import { PaymentFieldConfig } from "@evergiving/common/dist/es2015/form";

import { PaymentSourceKind } from "@evergiving/common/dist/es2015/pledge";
import { RadioGroup, RadioOptionSpec } from "@/lib/components/RadioGroup";
import { offeredPaymentSources } from "@/lib/misc/authorizedPaymentSource";

import { FormFieldsCreditCard } from "./FormFieldsCreditCard";
import { FormFieldsDirectDebit } from "./FormFieldsDirectDebit";

export type PaymentMethodProps = {
  autoFocus?: boolean;
  config: PaymentFieldConfig;
  onPaymentMethodChange: (newValue: PaymentSourceKind) => void;
  paymentSourceKind?: PaymentSourceKind;
  authorizedPaymentSource?: PaymentSourceKind;
};

export const PaymentMethod = ({
  autoFocus,
  config,
  onPaymentMethodChange,
  paymentSourceKind,
  authorizedPaymentSource,
}: PaymentMethodProps) => {
  // Which methods this form can offer, and the config block each is rendered
  // from. The decision lives in offeredPaymentSources so it can be tested; all
  // that is left here is drawing it.
  const offered = offeredPaymentSources(config, authorizedPaymentSource);

  // One method means no choice to present. A card or a direct debit renders its
  // fields; a wallet has none, because its button collects everything, and the
  // button itself is rendered by the submit area.
  if (offered.length === 1) {
    const only = offered[0];

    if (only.kind === PaymentSourceKind.CreditCard) {
      return <FormFieldsCreditCard creditCardFieldConfig={only.config} />;
    }

    if (only.kind === PaymentSourceKind.DirectDebit) {
      return <FormFieldsDirectDebit directDebitFieldConfig={only.config} />;
    }

    return null;
  }

  // Nothing to offer. A link naming a method this form cannot offer never
  // reaches here - IndexPage shows the expired-link page instead - so this is
  // the misconfigured case: a campaign with no payment method at all.
  if (offered.length === 0) {
    const wallet = config.applePay ?? config.googlePay;

    return wallet ? <p>{wallet.noWalletAvailableText}</p> : null;
  }

  const options: RadioOptionSpec<PaymentSourceKind>[] = offered.map(
    ({ kind, config: optionConfig }) => ({
      value: kind,
      content: () => <div>{optionConfig.optionText}</div>,
    })
  );

  return (
    <>
      <RadioGroup<PaymentSourceKind>
        className="paymentMethodSelector"
        value={paymentSourceKind}
        onChange={onPaymentMethodChange}
        ariaLabel="Payment Method"
        ariaRequired
        autoFocus={autoFocus}
      >
        {options}
      </RadioGroup>

      {config.creditCard !== undefined &&
        paymentSourceKind === PaymentSourceKind.CreditCard && (
          <FormFieldsCreditCard creditCardFieldConfig={config.creditCard} />
        )}
      {config.directDebit !== undefined &&
        paymentSourceKind === PaymentSourceKind.DirectDebit && (
          <FormFieldsDirectDebit directDebitFieldConfig={config.directDebit} />
        )}
    </>
  );
};
